import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Alert } from 'react-bootstrap';
import { Pencil, Check, X } from 'react-bootstrap-icons';
import { useFlashMessage } from '@/common/contexts/FlashMessageContext.tsx';

interface EditableBlockProps<T, F = unknown> {
  title: string;
  fetchData: () => Promise<T>;
  updateData: (formData: F) => Promise<T>;
  renderView: (props: { data: T; onEdit: () => void }) => React.ReactNode;
  renderEdit: (props: {
    data: T;
    onSubmit: (formData: F) => void;
    submitRef: React.RefObject<(() => void) | null>;
  }) => React.ReactNode;
}

const EditableBlock = <T, F = unknown>({
  title,
  fetchData,
  updateData,
  renderView,
  renderEdit,
}: EditableBlockProps<T, F>) => {
  const [isEditing, setIsEditing] = useState(false);
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const submitRef = useRef<(() => void) | null>(null);
  const { setFlashMessage } = useFlashMessage();

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await fetchData();
        setData(result);
      } catch (err) {
        console.error('Failed to load data:', err);
        setError('Failed to load data. Please refresh the page to try again.');
      } finally {
        setIsLoading(false);
      }
    };

    void loadData();
  }, [fetchData]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const isEmptyFormData = (formData: F): boolean => {
    if (formData === null || formData === undefined) return true;
    if (
      typeof formData === 'object' &&
      Object.keys(formData as object).length === 0
    )
      return true;
    return false;
  };

  const handleSubmit = async (formData: F) => {
    setIsLoading(true);
    setError(null);
    try {
      // Check if no changes were made
      if (isEmptyFormData(formData)) {
        setFlashMessage(
          `No changes were made to ${title.toLowerCase()}.`,
          'info'
        );
        setIsEditing(false);
        return;
      }

      const updatedData = await updateData(formData);
      setData(updatedData);
      setIsEditing(false);

      // Show success message
      setFlashMessage(`${title} updated successfully.`, 'success');
    } catch (err) {
      console.error('Failed to update data:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSaveClick = () => {
    if (submitRef.current) {
      submitRef.current();
    }
  };

  if (!data && !error && !isLoading) {
    return null;
  }

  const shouldShowEditButton = () => {
    return !isEditing && !error && !isLoading && data;
  };

  const renderCardHeader = () => (
    <Card.Header className="d-flex justify-content-between align-items-center rounded-top-4 shadow-sm">
      <h5 className="mb-0">{title}</h5>
      {shouldShowEditButton() && (
        <Button
          variant="outline-primary"
          size="sm"
          onClick={handleEdit}
          className="d-flex align-items-center gap-1"
        >
          <Pencil size={16} />
          Edit
        </Button>
      )}
    </Card.Header>
  );

  const renderCardBody = () => {
    if (error) {
      return <Alert variant="danger">{error}</Alert>;
    }

    if (isLoading && !data) {
      return <div className="text-center text-muted">Loading...</div>;
    }

    if (!data) {
      return null;
    }

    return (
      <>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        {isEditing ? (
          <>
            {renderEdit({
              data,
              onSubmit: handleSubmit,
              submitRef,
            })}
            <div className="d-flex gap-2 mt-3 pt-3 border-top">
              <Button
                variant="primary"
                onClick={handleSaveClick}
                disabled={isLoading}
                className="d-flex align-items-center gap-1"
              >
                <Check size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="outline-warning"
                onClick={handleCancel}
                disabled={isLoading}
                className="d-flex align-items-center gap-1"
              >
                <X size={16} />
                Cancel
              </Button>
            </div>
          </>
        ) : (
          renderView({
            data,
            onEdit: handleEdit,
          })
        )}
      </>
    );
  };

  return (
    <Card className="shadow-sm rounded-4">
      {renderCardHeader()}
      <Card.Body className="rounded-bottom-4 bg-light-subtle">
        {renderCardBody()}
      </Card.Body>
    </Card>
  );
};

export default EditableBlock;
